import { useState } from 'react';
import type { OpsUserRecord } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useDisableOpsUser,
  useEnableOpsUser,
  useOpsUsers,
  useResetOpsUserPassword,
} from '../api/opsUsers.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';

/**
 * `/ops-users` — admin page for the ops user directory. Epic #109 P3.18.
 *
 * Three actions per row: disable / enable (mutually exclusive based on
 * current state) + reset password. Reset password is type-to-confirm
 * because it invalidates live sessions and deletes the credential row;
 * the email doubles as the confirmation token.
 */

export function OpsUsersPage(): JSX.Element {
  const t = useT();
  const query = useOpsUsers();

  return (
    <section className="flex flex-col gap-8" data-testid="ops-users-page">
      <header className="flex flex-col gap-3">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('ops_users.title')}
        </h2>
        <p className="text-sm text-on-surface-variant">{t('ops_users.subtitle')}</p>
      </header>

      <article
        data-testid="ops-users-list-card"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      >
        {query.isLoading ? (
          <p className="text-xs text-outline" data-testid="ops-users-loading">
            {t('ops_users.loading')}
          </p>
        ) : query.isError ? (
          <p
            className="text-xs text-error"
            role="alert"
            data-testid="ops-users-error"
          >
            {t('ops_users.error')}
          </p>
        ) : (query.data ?? []).length === 0 ? (
          <p className="text-xs text-outline" data-testid="ops-users-empty">
            {t('ops_users.empty')}
          </p>
        ) : (
          <UsersTable users={query.data ?? []} />
        )}
      </article>
    </section>
  );
}

interface UsersTableProps {
  readonly users: readonly OpsUserRecord[];
}

function UsersTable({ users }: UsersTableProps): JSX.Element {
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs" data-testid="ops-users-table">
        <thead>
          <tr className="border-b border-outline-variant text-[10px] uppercase tracking-[0.08em] text-outline">
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('ops_users.column.email')}
            </th>
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('ops_users.column.roles')}
            </th>
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('ops_users.column.status')}
            </th>
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('ops_users.column.created_at')}
            </th>
            <th scope="col" className="py-2 pr-4 text-right font-semibold">
              {t('ops_users.column.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <UserRow key={u.id} user={u} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface UserRowProps {
  readonly user: OpsUserRecord;
}

function UserRow({ user }: UserRowProps): JSX.Element {
  const t = useT();
  const disable = useDisableOpsUser();
  const enable = useEnableOpsUser();
  const reset = useResetOpsUserPassword();
  const toast = useToast();
  const isDisabled = user.disabled_at !== null;
  const busy = disable.isPending || enable.isPending || reset.isPending;

  const [confirmReset, setConfirmReset] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleDisable(): Promise<void> {
    setError(null);
    try {
      await disable.mutateAsync(user.id);
      toast.success(t('ops_users.toast.disabled'));
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }
  async function handleEnable(): Promise<void> {
    setError(null);
    try {
      await enable.mutateAsync(user.id);
      toast.success(t('ops_users.toast.enabled'));
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }
  async function handleReset(): Promise<void> {
    setError(null);
    try {
      const res = await reset.mutateAsync(user.id);
      toast.success(
        t('ops_users.toast.reset', {
          sessions: String(res.sessions_invalidated),
        }),
      );
      setConfirmReset(false);
      setTyped('');
    } catch (err) {
      const m = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(m);
      toast.error(m);
    }
  }

  return (
    <tr
      className="border-b border-outline-variant/40"
      data-testid={`ops-users-row-${user.id}`}
      data-disabled={isDisabled ? 'true' : 'false'}
    >
      <td className="py-2 pr-4 text-on-surface">{user.email}</td>
      <td className="py-2 pr-4">
        <div className="flex flex-wrap gap-1">
          {user.roles.length === 0 ? (
            <span className="text-[11px] text-outline">—</span>
          ) : (
            user.roles.map((r) => (
              <span
                key={r}
                className="inline-flex items-center rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-bold uppercase text-on-surface"
              >
                {r}
              </span>
            ))
          )}
        </div>
      </td>
      <td className="py-2 pr-4">
        <span
          className={
            isDisabled
              ? 'inline-flex items-center rounded-full bg-tertiary-container px-2 py-0.5 text-[10px] font-bold uppercase text-on-tertiary-container'
              : 'inline-flex items-center rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-bold uppercase text-on-surface'
          }
        >
          {isDisabled ? t('ops_users.status.disabled') : t('ops_users.status.active')}
          {!user.has_password ? ` · ${t('ops_users.status.no_password')}` : ''}
        </span>
      </td>
      <td className="py-2 pr-4 font-mono text-[11px] text-outline">{user.created_at}</td>
      <td className="py-2 pr-4 text-right">
        <div className="flex flex-col items-end gap-2">
          {isDisabled ? (
            <button
              type="button"
              onClick={() => void handleEnable()}
              disabled={busy}
              data-testid={`ops-users-enable-${user.id}`}
              className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 disabled:pointer-events-none"
            >
              {enable.isPending ? t('ops_users.action.enabling') : t('ops_users.action.enable')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleDisable()}
              disabled={busy}
              data-testid={`ops-users-disable-${user.id}`}
              className="inline-flex items-center justify-center rounded-lg bg-tertiary-container px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-tertiary-container transition-colors hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 disabled:pointer-events-none"
            >
              {disable.isPending
                ? t('ops_users.action.disabling')
                : t('ops_users.action.disable')}
            </button>
          )}
          {confirmReset ? (
            <div
              className="flex flex-col gap-2 rounded-lg bg-surface-container-low p-3"
              data-testid={`ops-users-reset-confirm-${user.id}`}
            >
              <p className="text-[11px] text-outline">
                {t('ops_users.reset.confirm_prompt', { email: user.email })}
              </p>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.currentTarget.value)}
                data-testid={`ops-users-reset-typed-${user.id}`}
                placeholder={user.email}
                className="rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-1 text-sm text-on-surface focus:border-primary focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmReset(false);
                    setTyped('');
                    setError(null);
                  }}
                  data-testid={`ops-users-reset-cancel-${user.id}`}
                  className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface hover:bg-surface-bright"
                >
                  {t('ops_users.action.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void handleReset()}
                  disabled={busy || typed !== user.email}
                  data-testid={`ops-users-reset-submit-${user.id}`}
                  className="inline-flex items-center justify-center rounded-lg bg-error px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-error disabled:opacity-40 disabled:pointer-events-none"
                >
                  {reset.isPending
                    ? t('ops_users.action.resetting')
                    : t('ops_users.action.reset_confirm')}
                </button>
              </div>
              {error !== null ? (
                <p className="text-[11px] text-error" role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              disabled={busy}
              data-testid={`ops-users-reset-${user.id}`}
              className="inline-flex items-center justify-center rounded-lg bg-surface-container-lowest px-3 py-1.5 text-[11px] font-semibold text-on-surface-variant underline transition-colors hover:text-on-surface"
            >
              {t('ops_users.action.reset_password')}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
